import React from 'react';
import { auditLogAPI } from '../services/api';

type AuditLogEntry = {
  timestamp: number;
  type: string;
  message: string;
  shortDescription?: string;
  data?: any;
  performedBy?: string;
  performedById?: number;
};

type TruppRef = {
  id: number;
  name: string;
};

interface TruppAuditlogModalProps {
  truppId?: number;
  truppName?: string;
  trupps?: TruppRef[];
  onClose: () => void;
  auditlog?: AuditLogEntry[]; // Parent's audit log state (primary source)
}

const formatDateTime = (timestamp: number) => {
  let date: Date;

  if (typeof timestamp === 'string') {
    date = new Date(timestamp);
  } else if (typeof timestamp === 'number') {
    if (timestamp < 10000000000) {
      date = new Date(timestamp * 1000);
    } else {
      date = new Date(timestamp);
    }
  } else {
    date = new Date();
  }

  if (isNaN(date.getTime())) {
    return 'Ungültiges Datum';
  }

  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }) + ' ' + date.toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

const formatCountdown = (seconds: number) => {
  const absSec = Math.abs(seconds);
  const sign = seconds < 0 ? '-' : '';
  const m = Math.floor(absSec / 60).toString().padStart(2, '0');
  const s = (absSec % 60).toString().padStart(2, '0');
  return `${sign}${m}:${s}`;
};

const renderEntryMessage = (entry: any) => {
  const aufgabe = entry.data?.aufgabe || entry.data?.task || '-';
  const standort = entry.data?.standort || entry.data?.location || '-';
  const druck = typeof entry.data?.bar === 'number' ? `${entry.data.bar} Bar` :
    typeof entry.data?.startPressure === 'number' ? `${entry.data.startPressure} Bar` : '-';
  const funkkanal = entry.data?.funkkanal || entry.data?.radioChannel || '-';

  if (entry.type === 'Erfasst' || entry.type === 'Trupp erstellt') {
    return (<><b>Erfasst:</b> Trupp wurde erfasst</>);
  }
  if (entry.type === 'Einsatzstart' || entry.shortDescription === 'In Einsatz geschickt' || entry.type === 'Trupp gestartet') {
    return (<><b>Einsatzstart:</b> {aufgabe}, {standort}, {druck}, {funkkanal}</>);
  }
  if (entry.type === 'Status geändert') {
    let abfrage = '';
    if (typeof entry.data?.abfrageCountdown === 'number') abfrage = ` (Abfrage ${formatCountdown(entry.data.abfrageCountdown)})`;
    return (<><b>Status aktualisiert:</b> {aufgabe}, {standort}, {druck}, {funkkanal}{abfrage}</>);
  }
  if (entry.type === 'Bearbeiten') {
    return (<><b>Status bearbeitet:</b> {aufgabe}, {standort}, {druck}, {funkkanal}</>);
  }
  if (entry.type === 'Mayday') {
    if (entry.message && entry.message.includes('beendet')) {
      return (<><b>Mayday:</b> Mayday wurde beendet</>);
    }
    return (<><b>Mayday:</b> Mayday wurde ausgelöst</>);
  }
  if (entry.type === 'Trupp aus Einsatz' || entry.shortDescription === 'Trupp aus Einsatz') {
    return (<><b>Aus Einsatz:</b> Trupp wurde aus Einsatz genommen</>);
  }
  if (entry.type === 'Trupp reaktiviert') {
    return (<><b>Reaktiviert:</b> Trupp wurde reaktiviert</>);
  }
  if (entry.type === 'Druckanpassung') {
    return entry.data?.previousPressure != null
      ? (<><b>Druckanpassung:</b> {entry.data.previousPressure} → {entry.data?.finalPressure} Bar</>)
      : (<><b>Druckanpassung:</b> Finaler Druck angepasst: {entry.data?.finalPressure} Bar</>);
  }
  if (entry.type === 'Mitglieder geändert') {
    const added = entry.data?.added || [];
    const removed = entry.data?.removed || [];
    const addedText = added.length > 0 ? `Hinzugefügt: ${added.map((m: any) => m.vorname + ' ' + m.nachname).join(', ')}` : '';
    const removedText = removed.length > 0 ? `Entfernt: ${removed.map((m: any) => m.vorname + ' ' + m.nachname).join(', ')}` : '';
    return (<><b>Mitglieder geändert:</b> {addedText} {removedText}</>);
  }
  if (entry.type === 'Truppführer geändert') {
    return (<><b>Truppführer geändert:</b> Neuer TF: {entry.data?.neuerTF?.vorname} {entry.data?.neuerTF?.nachname}</>);
  }
  if (entry.type === 'Rückmeldung') {
    return (<><b>Rückmeldung:</b> {entry.message}</>);
  }
  if (entry.type === 'Alarm stummgeschaltet') {
    return (<><b>Alarm stummgeschaltet</b>: Alarm wurde manuell stummgeschaltet.</>);
  }
  if (entry.type === 'Alarm aktiviert') {
    return entry.data?.auto
      ? (<><b>Alarm aktiviert</b>: Stummschaltung automatisch nach 2 Min. aufgehoben.</>)
      : (<><b>Alarm aktiviert</b>: Alarm wurde manuell aktiviert.</>);
  }
  if (entry.type === 'Benutzerwechsel') {
    return (<><b>Benutzerwechsel</b>: Benutzer gewechselt zu <i>{entry.data?.newUser || 'Unbekannt'}</i>{entry.data?.manual ? ' (manuell)' : ''}</>);
  }
  if (entry.type === 'Trupp verschoben') {
    return (<><b>Verschoben:</b> {entry.message}</>);
  }
  if (entry.type === 'Funkkanal') {
    return (<><b>Funkkanal:</b> gewechselt auf {funkkanal}</>);
  }
  return (<>{entry.type}: {entry.message}</>);
};

const TruppAuditlogModal: React.FC<TruppAuditlogModalProps> = ({ truppId, truppName, trupps, onClose, auditlog: parentAuditlog }) => {
  const [entries, setEntries] = React.useState<any[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const multiMode = Array.isArray(trupps) && trupps.length > 0;
  const truppIds = React.useMemo(
    () => multiMode ? trupps!.map((t) => t.id) : (truppId != null ? [truppId] : []),
    [multiMode, trupps, truppId]
  );
  const truppNameById = React.useMemo(() => {
    const map = new Map<number, string>();
    if (multiMode) {
      trupps!.forEach((t) => map.set(t.id, t.name));
    } else if (truppId != null) {
      map.set(truppId, truppName || `Trupp ${truppId}`);
    }
    return map;
  }, [multiMode, trupps, truppId, truppName]);

  React.useEffect(() => {
    const loadEntries = async () => {
      setIsLoading(true);
      try {
        let allLogs: any[] = [];

        if (parentAuditlog && parentAuditlog.length > 0) {
          allLogs = [...parentAuditlog];
        }

        if (navigator.onLine) {
          try {
            const opsID = parseInt(window.location.pathname.split('/')[2]);
            if (!isNaN(opsID)) {
              const logs = await auditLogAPI.getAuditLog(opsID);
              const apiLogs = logs.map((log: any) => {
                let timestamp: number;
                if (log.created_at) {
                  if (typeof log.created_at === 'string') {
                    timestamp = new Date(log.created_at).getTime();
                  } else if (typeof log.created_at === 'number') {
                    timestamp = log.created_at;
                  } else {
                    timestamp = Date.now();
                  }
                } else {
                  timestamp = Date.now();
                }

                let data = undefined;
                if (log.logData) {
                  if (typeof log.logData === 'string') {
                    try {
                      data = JSON.parse(log.logData);
                    } catch (e) {
                      data = { raw: log.logData };
                    }
                  } else {
                    data = log.logData;
                  }
                }

                if (log.teamID) {
                  if (!data) data = {};
                  if (!data.truppId) data.truppId = log.teamID;
                }

                return {
                  type: log.logType,
                  message: log.message,
                  shortDescription: log.shortDescription,
                  timestamp: timestamp,
                  data: data,
                  truppId: log.teamID,
                  performedBy: data?.performedBy,
                  performedById: data?.performedById,
                };
              });

              for (const apiLog of apiLogs) {
                const isDuplicate = allLogs.some(existing =>
                  Math.abs((existing.timestamp || 0) - (apiLog.timestamp || 0)) < 2000 &&
                  existing.type === apiLog.type &&
                  existing.message === apiLog.message
                );
                if (!isDuplicate) {
                  allLogs.push(apiLog);
                }
              }
            }
          } catch (error) {
            console.warn('Failed to load audit logs from API:', error);
          }
        }

        const localAuditLogs = JSON.parse(localStorage.getItem('auditlog') || '[]');
        if (localAuditLogs.length > 0) {
          for (const localLog of localAuditLogs) {
            const isDuplicate = allLogs.some(existing =>
              Math.abs((existing.timestamp || 0) - (localLog.timestamp || 0)) < 2000 &&
              existing.type === localLog.type &&
              existing.message === localLog.message
            );
            if (!isDuplicate) {
              allLogs.push(localLog);
            }
          }
        }

        const idSet = new Set(truppIds);
        const teamLogs = allLogs.filter((log: any) => {
          const id = log.truppId ?? log.data?.truppId ?? log.data?.teamId;
          return id != null && idSet.has(id);
        }).map((log: any) => {
          const id = log.truppId ?? log.data?.truppId ?? log.data?.teamId;
          return {
            ...log,
            resolvedTruppId: id,
            resolvedTruppName: truppNameById.get(id) || log.data?.name || `Trupp ${id}`
          };
        });

        teamLogs.sort((a: any, b: any) => (a.timestamp || 0) - (b.timestamp || 0));
        setEntries(teamLogs);
      } catch (error) {
        console.error('Error loading audit logs:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadEntries();
  }, [truppIds, parentAuditlog, truppNameById]);

  const groupedEntries = React.useMemo(() => {
    if (!multiMode) {
      return [{ id: truppId, name: truppName || 'Trupp', entries }];
    }
    const groups: { id: number; name: string; entries: any[] }[] = [];
    const byId = new Map<number, any[]>();
    entries.forEach((entry) => {
      const id = entry.resolvedTruppId;
      if (!byId.has(id)) byId.set(id, []);
      byId.get(id)!.push(entry);
    });
    trupps!.forEach((t) => {
      groups.push({ id: t.id, name: t.name, entries: byId.get(t.id) || [] });
    });
    // Orphan logs for unknown/deleted troops
    byId.forEach((list, id) => {
      if (!trupps!.some((t) => t.id === id) && list.length > 0) {
        groups.push({ id, name: list[0].resolvedTruppName || `Trupp ${id}`, entries: list });
      }
    });
    return groups;
  }, [multiMode, entries, trupps, truppId, truppName]);

  const title = multiMode ? 'Trupp-Journal' : `Auditlog: ${truppName}`;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 12,
        padding: 24,
        width: 'min(720px, 94vw)',
        maxHeight: '80vh',
        overflow: 'auto',
        boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
      }}>
        <h3 style={{ margin: '0 0 16px 0', color: '#c00', textAlign: 'center' }}>{title}</h3>
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: 20 }}>Lade Journal...</div>
        ) : entries.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 20, color: '#666' }}>Keine Journal-Einträge gefunden</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            {groupedEntries.map((group) => (
              <div key={group.id}>
                {multiMode && (
                  <div style={{
                    background: '#f5f5f5',
                    borderLeft: '4px solid #c00',
                    borderRadius: 8,
                    padding: '8px 12px',
                    marginBottom: 8,
                    fontWeight: 800,
                    fontSize: 15,
                    color: '#111'
                  }}>
                    {group.name}
                    <span style={{ marginLeft: 8, fontWeight: 500, color: '#888', fontSize: 12 }}>
                      {group.entries.length} Einträge
                    </span>
                  </div>
                )}
                {group.entries.length === 0 ? (
                  <div style={{ color: '#999', fontSize: 13, padding: '4px 4px 8px' }}>Keine Einträge</div>
                ) : (
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {group.entries.map((entry: any, i: number) => {
                      const performer = entry.performedBy || entry.data?.performedBy;
                      return (
                        <li key={`${group.id}-${i}`} style={{ marginBottom: 8, borderBottom: '1px solid #eee', paddingBottom: 4 }}>
                          <span style={{ color: '#888', fontSize: 12, marginRight: 8 }}>{formatDateTime(entry.timestamp)}</span>
                          {renderEntryMessage(entry)}
                          {performer && (
                            <span style={{ color: '#999', fontSize: 11, marginLeft: 6, fontStyle: 'italic' }}>
                              – {performer}
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}
        <div style={{ textAlign: 'center', width: '100%' }}>
          <button onClick={onClose} style={{ marginTop: 16, background: '#eee', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 700 }}>Schliessen</button>
        </div>
      </div>
    </div>
  );
};

export default TruppAuditlogModal;
